import { OverlayPage, usePermissionsContext } from '@carboncopyco/components';
import { useUpdateDemoMutation } from '@carboncopyco/data';
import {
  type IDemo,
  PermissionAction,
  PermissionKey,
} from '@carboncopyco/types';
import { useDemoAnalytics, useWorkspaceAnalytics } from '@carboncopyco/utils';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import GiveADemoModalExtended from '../components/GiveADemoModalExtended';
import WorkspaceLayout from '../components/Workspaces/WorkspaceLayout';
import { useCustomNavigate } from '../hooks/useCustomNavigate';
import { useDemoCommons } from '../hooks/useDemoCommons';
import { usePageEvent } from '../hooks/usePageEvent';

export const OverlayNosScreen = ({ demo }: { demo: IDemo }) => {
  const {
    handlePresent,
    workspace,
    workspaceId,
    demoId,
    organizationId,
    showCreatePresentation,
    setShowCreatePresentation,
    goToDemoPreview,
  } = useDemoCommons({ demo });
  const { trackDemoClickOpenCopyToWorkspaceModal } =
    useWorkspaceAnalytics('demo_page');
  const { trackDemoClickCreateTour } = useDemoAnalytics('demo_page');
  const { getPermissions } = usePermissionsContext();
  const { search } = useLocation();

  const {
    navigateToEditor,
    navigateToTourCapture,
    navigateToDemoPage,
    navigateToDemoPageByWorkspace,
    navigateToWorkspace,
    navigateToFolder,
    links,
  } = useCustomNavigate();

  const updateDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.updateDemoById
  );

  const [updateDemo] = useUpdateDemoMutation();
  usePageEvent();

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const giveADemoParam = searchParams.get('giveADemo');
    if (giveADemoParam) {
      setShowCreatePresentation(true);
    }
  }, []);

  if (!demo || !demoId || !workspaceId) {
    return null;
  }

  return (
    <WorkspaceLayout>
      <OverlayPage
        rootPath={links.getDemoPageRoot(Number(demoId))}
        workspaceId={workspaceId}
        organizationId={organizationId}
        demoId={demoId}
        updateDemo={() =>
          updateDemoPermission
            ? (value: any) =>
                updateDemo({
                  organizationId,
                  workspaceId,
                  id: demoId,
                  demoName: value,
                })
            : undefined
        }
        goToLinksTab={() => navigateToDemoPage(demoId, workspaceId, 'links')}
        goToDemoPreview={goToDemoPreview}
        handleCreatePresentation={() => setShowCreatePresentation(true)}
        goToEditor={() => navigateToEditor(Number(demoId))}
        handleCreateTour={() => {
          trackDemoClickCreateTour(demo);
          navigateToTourCapture({
            demoId: demo?.id,
            folderId: demo?.folderId,
          });
        }}
        goBack={() => navigateToWorkspace(demo.workspaceId)}
        goToDuplicatedDemo={({ id }) =>
          navigateToDemoPage(String(id), workspaceId)
        }
        goToCopiedDemo={(demoId, targetWorkspaceId) => {
          trackDemoClickOpenCopyToWorkspaceModal({
            workspace,
          });
          navigateToDemoPageByWorkspace(
            String(demoId),
            String(targetWorkspaceId)
          );
        }}
        goToWorkspacePage={() => navigateToWorkspace(demo.workspaceId)}
        goToFolderPage={navigateToFolder}
        clonePath={links.getClonePageLink(demo?.recordId)}
        goToPresentation={handlePresent}
      />
      {showCreatePresentation && demo && (
        <GiveADemoModalExtended
          open
          workspaceId={workspaceId}
          entity={demo}
          onPresent={() => setShowCreatePresentation(false)}
          onSave={() => {
            navigateToDemoPage(demoId, workspaceId, 'presentations');
            setShowCreatePresentation(false);
          }}
          onClose={() => setShowCreatePresentation(false)}
        />
      )}
    </WorkspaceLayout>
  );
};
